import { z } from "zod";
import { PositionStatus } from "./enums/position-status";
import { TokenSchema } from "./pool";
import { bigNumberTransformer } from "./transformers/big-number-transformer";
import { dateTransformer } from "./transformers/date-transformer";

const PositionPoolSchema = z.object({
  id: z.string(),
});

export const PositionBase = z.object({
  id: z.string(),
  status: z.enum(PositionStatus),
  sold_quote: bigNumberTransformer,
  bought_quote: bigNumberTransformer,
  sold_base: bigNumberTransformer,
  bought_base: bigNumberTransformer,
  sold_usd: bigNumberTransformer,
  bought_usd: bigNumberTransformer,
  orders_count: z.number(),
  avg_purchase_price_quote: bigNumberTransformer,
  avg_purchase_price_usd: bigNumberTransformer,
  avg_exit_price_quote: bigNumberTransformer,
  avg_exit_price_usd: bigNumberTransformer,
  closed_at: dateTransformer.nullish(),
  created_at: dateTransformer,
  total_fees: bigNumberTransformer,
  total_fees_usd: bigNumberTransformer,
  realized_pnl_quote: bigNumberTransformer,
  realized_pnl_usd: bigNumberTransformer,
  realized_pnl_percentage: bigNumberTransformer,
  organisation_id: z.string().uuid(),
  token: TokenSchema,
  pools: z.array(PositionPoolSchema),
});

export const PositionSchema = PositionBase.transform((position) => {
  const unclosed_realized_pnl_quote = position.realized_pnl_quote;
  const unclosed_realized_pnl_usd = position.realized_pnl_usd;
  const remaining_base = position.bought_base - position.sold_base;

  const unrealizedPnl = (currentPricePair: number, nativePriceUsd: number) => {
    const native =
      remaining_base * (currentPricePair - position.avg_purchase_price_quote);
    const usd = native * nativePriceUsd;

    const percentage =
      ((currentPricePair - position.avg_purchase_price_quote) /
        position.avg_purchase_price_quote) *
      100;

    return { native, usd, percentage: isNaN(percentage) ? 0 : percentage };
  };

  const totalPnl = (currentPricePair: number, nativePriceUsd: number) => {
    const unrealized = unrealizedPnl(currentPricePair, nativePriceUsd);
    const realizedUsd = position.realized_pnl_usd;
    const realizedNative = position.realized_pnl_quote;

    const totalPnlUsd = unrealized.usd + realizedUsd;
    const totalPnlNative = unrealized.native + realizedNative;
    const totalCostUsd = position.sold_usd;
    const pnlPercent =
      totalCostUsd === 0 ? 0 : (totalPnlUsd / totalCostUsd) * 100;

    return { usd: totalPnlUsd, native: totalPnlNative, percentage: pnlPercent };
  };

  return {
    ...position,
    unclosed_realized_pnl_quote,
    unclosed_realized_pnl_usd,
    remaining_base,
    unrealizedPnl,
    totalPnl,
    pool_id: position.pools.length ? position.pools[0].id : "",
  };
});

export type Position = z.infer<typeof PositionSchema>;

export const PartialPositionSchema = PositionBase.partial();

export type PartialPosition = z.infer<typeof PartialPositionSchema>;
